import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { batchLevel, createTextureBank, dressSurfaces } from './materials.js';
import { createFixtures } from './fixtures.js';
import { createDressing } from './dressing.js';
import { createVegetation } from './vegetation.js';
import { createSite } from './site.js';
import { createBallroom } from './ballroom.js';
import { createFurniture } from './furniture.js';
import { createSky, skyAt, sunAt } from './sky.js';
import { LIGHTS, lightOf } from './rooms.js';

/**
 * The look of the building: its surfaces, its light, and the sky over it.
 *
 * ---------------------------------------------------------------------------
 * IT READS. IT DOES NOT WRITE.
 *
 * The contract the rig and the soundboard have, for the same reason. This
 * layer imports nothing from `src/sim` or `src/data`. It is handed a plain copy
 * of the level's description once, at boot, and once a frame a reading made of
 * numbers — the minute of the day, where the camera is and which room that is,
 * how far the ballroom is set. It holds no reference to the player, the clock,
 * the setup or anything else that could be changed by touching it.
 *
 * What it does change is how things are drawn: materials, texture
 * coordinates, lights, the fog and the sky. The boxes the game collides with
 * were built from the same level data before any of this ran, live in
 * `level.solids` and `level.world`, and are never handed here.
 * `scripts/look.mjs` walks the running game to check that none of them moved.
 * ---------------------------------------------------------------------------
 */

const FT = 0.3048;
/** Half the width of the sun's shadow, in metres; the same span the greybox had. */
const SHADOW_SPAN = 130 * FT;

export function createLook({ scene, renderer, lights, levelObject, plan }) {
  const bank = createTextureBank({ anisotropy: Math.min(4, renderer.capabilities.getMaxAnisotropy?.() ?? 1) });
  // A soft room to reflect, for the things that shine. Built from boxes and
  // lights in code, prefiltered once at boot.
  const pmrem = new THREE.PMREMGenerator(renderer);
  const room = new RoomEnvironment();
  const environment = pmrem.fromScene(room, 0.04).texture;
  room.dispose?.();
  pmrem.dispose();
  const surfaces = dressSurfaces(levelObject, plan, bank, { environment });

  // Ceilings shade what is under them now that every room brings its own light.
  // Before, a ceiling that cast put the building in the dark.
  for (const mesh of levelObject.children) if (mesh.name.startsWith('ceiling:')) mesh.castShadow = true;

  const sky = createSky({ ground: bank.surface('gravel') });
  scene.add(sky.object3D);
  const fixtures = createFixtures(plan, { environment });
  scene.add(fixtures.object3D);
  const dressing = createDressing({ plan, levelObject, environment });
  scene.add(dressing.object3D);
  const vegetation = createVegetation({ plan, levelObject });
  scene.add(vegetation.object3D);
  const site = createSite({ plan, bank });
  scene.add(site.object3D);
  const ballroom = createBallroom({ plan, environment });
  scene.add(ballroom.object3D);
  const furniture = createFurniture({ plan, levelObject, bank, environment });
  scene.add(furniture.object3D);
  // Last, once everything that hides a box has hidden it.
  const batch = batchLevel(levelObject, new Set(surfaces.materials));
  scene.add(batch.object3D);

  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;
  scene.background = null;

  const { ambient, key } = lights;
  const hemisphere = new THREE.HemisphereLight(0xbcd4ea, 0x6a5440, 0);
  hemisphere.name = 'look-sky-fill';
  scene.add(hemisphere);
  scene.add(key.target);
  const texel = (SHADOW_SPAN * 2) / key.shadow.mapSize.x;

  const roomsById = new Map(plan.rooms.map((room) => [room.id, room]));
  // What the ambient is heading for, and where it is: eased so walking through
  // a door is a change of light and not a cut.
  const ambientNow = { colour: new THREE.Color(0x9fa8bd), level: 0.6 };
  const moon = new THREE.Vector3(-0.35, 0.8, 0.5).normalize();
  const scratch = new THREE.Color();
  let last = null;

  const ballroomFill = surfaces.byLight.get('chandelier') ?? [];
  let lastSet = -1;

  function update({ minutes, camera, room, dt = 1 / 60, ballroomSet = 0 }) {
    const sun = sunAt(minutes);
    const look = skyAt(sun.elevation);
    const here = roomsById.get(room) ?? null;
    const light = lightOf(here);

    // The sky, and the fill it gives anybody standing out under it.
    sky.update({ camera, sun, sky: look });
    hemisphere.color.copy(look.zenith).lerp(look.horizon, 0.5);
    hemisphere.intensity = light === 'outdoors' ? 1.1 * look.sky : 0.15 * look.sky;

    // The ambient: the room's own, indoors; the sky's, outside.
    const target = light === 'outdoors'
      ? { colour: scratch.copy(look.horizon).lerp(look.zenith, 0.4), level: 0.25 + 0.45 * look.sky }
      : { colour: scratch.set(LIGHTS[light].ambient.colour), level: LIGHTS[light].ambient.level };
    const ease = 1 - Math.exp(-dt * 6);
    ambientNow.colour.lerp(target.colour, ease);
    ambientNow.level += (target.level - ambientNow.level) * ease;
    ambient.color.copy(ambientNow.colour);
    ambient.intensity = ambientNow.level;

    // The sun, or the moon once it is down, aimed at wherever the camera is so
    // the shadows are sharp where you are looking — snapped to the shadow
    // map's texels so they do not crawl as you walk.
    const toward = sun.elevation > -2 ? new THREE.Vector3(sun.direction.x, sun.direction.y, sun.direction.z) : moon;
    const tx = Math.round(camera.x / texel) * texel;
    const tz = Math.round(camera.z / texel) * texel;
    key.target.position.set(tx, 0, tz);
    key.position.set(tx + toward.x * 90, toward.y * 90, tz + toward.z * 90);
    key.target.updateMatrixWorld();
    key.color.copy(look.sunColour);
    key.intensity = look.sun;

    // Haze toward the horizon's colour.
    if (scene.fog) {
      scene.fog.color.copy(look.horizon).lerp(look.zenith, 0.25);
      scene.fog.near = 90;
      scene.fog.far = 380;
    }

    // The ballroom, working or dressed for the evening.
    if (ballroomSet !== lastSet) {
      lastSet = ballroomSet;
      const lit = ballroom.update({ set: ballroomSet });
      fixtures.setChandeliers(lit.chandelier);
      for (const made of ballroomFill) made.lightMapIntensity = LIGHTS.chandelier.fill * lit.fill;
    }
    fixtures.update({ camera, dark: look.dark });
    dressing.update({ minutes });
    last = { elevation: sun.elevation, dark: look.dark, light, sun: look.sun, ballroomSet };
  }

  return {
    update,
    /** What the last frame was lit as. For the headless check; a copy. */
    get reading() {
      return last ? { ...last } : null;
    },
    fixtures: fixtures.fixtures,
    /** How many boxes were batched, into how many meshes. */
    batched: { boxes: batch.batched, meshes: batch.meshes },
    dispose() {
      sky.dispose();
      fixtures.dispose();
      dressing.dispose();
      vegetation.dispose();
      site.dispose();
      ballroom.dispose();
      furniture.dispose();
      batch.dispose();
      surfaces.dispose();
      bank.dispose();
      environment.dispose();
      scene.remove(hemisphere);
    },
  };
}
